import { z } from 'zod';
import { KZ_TRANSFERS, kzHolidays } from './schedule-calendar';

/* ─────────── Срок ответа из текста письма (ДК-01, ДК-03) ─────────── */

const transferred = new Set(Object.values(KZ_TRANSFERS));
const holidayYears = new Map<number, Map<string, string>>();

/** Рабочий ли день по производственному календарю РК (без выходных и праздников, с переносами) */
export function isKzWorkDay(date: string): boolean {
  if (transferred.has(date)) return true;
  const y = Number(date.slice(0, 4));
  let hol = holidayYears.get(y);
  if (!hol) {
    hol = kzHolidays(y);
    holidayYears.set(y, hol);
  }
  if (hol.has(date)) return false;
  const wd = new Date(`${date}T00:00:00Z`).getUTCDay();
  return wd !== 0 && wd !== 6;
}

function addDays(date: string, n: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Дата через N рабочих дней после даты письма (сама дата письма не считается) */
export function addKzWorkDays(from: string, n: number): string {
  let d = from;
  let left = n;
  while (left > 0) {
    d = addDays(d, 1);
    if (isKzWorkDay(d)) left--;
  }
  return d;
}

/** Если срок выпал на нерабочий день — следующий рабочий */
function nextWorkDay(date: string): string {
  let d = date;
  while (!isKzWorkDay(d)) d = addDays(d, 1);
  return d;
}

export const LetterTermSuggestion = z.object({
  /** Предложенный срок ответа */
  date: z.iso.date(),
  /** Как найден: через N рабочих или календарных дней либо названной датой */
  kind: z.enum(['working_days', 'calendar_days', 'date']),
  days: z.number().int().nullable(),
  /** Фраза из письма, по которой сделано предложение */
  quote: z.string(),
});
export type LetterTermSuggestion = z.infer<typeof LetterTermSuggestion>;

export const LetterTermInput = z.object({
  text: z.string().min(1).max(20000),
  /** Дата письма — от неё считаются дни */
  letterDate: z.iso.date(),
});

const MONTHS: Record<string, number> = {
  января: 1,
  февраля: 2,
  марта: 3,
  апреля: 4,
  мая: 5,
  июня: 6,
  июля: 7,
  августа: 8,
  сентября: 9,
  октября: 10,
  ноября: 11,
  декабря: 12,
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
  қаңтар: 1,
  ақпан: 2,
  наурыз: 3,
  сәуір: 4,
  мамыр: 5,
  маусым: 6,
  шілде: 7,
  тамыз: 8,
  қыркүйек: 9,
  қазан: 10,
  қараша: 11,
  желтоқсан: 12,
};

const WORDS: Record<string, number> = {
  один: 1,
  одного: 1,
  два: 2,
  двух: 2,
  три: 3,
  трёх: 3,
  трех: 3,
  четыре: 4,
  четырёх: 4,
  четырех: 4,
  пять: 5,
  пяти: 5,
  шесть: 6,
  шести: 6,
  семь: 7,
  семи: 7,
  восемь: 8,
  десять: 10,
  десяти: 10,
  пятнадцать: 15,
  пятнадцати: 15,
  двадцать: 20,
  тридцать: 30,
  тридцати: 30,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  ten: 10,
  fifteen: 15,
  thirty: 30,
};

const NUM = '(\\d{1,3}|[а-яёa-z]{3,14})';
// «рабочих» / working / business / жұмыс — рабочие дни; «календарных» / calendar / күнтізбелік — календарные
const WORKING = /(рабоч|раб\.?|р\.\s?д|банков|working|business|жұмыс)/i;
const DAYS = '(?:дн(?:ей|я|ь)|суток|days?|күні?)';

function num(raw: string): number | null {
  if (/^\d+$/.test(raw)) return Number(raw);
  return WORDS[raw.toLowerCase()] ?? null;
}

function validDate(y: number, m: number, d: number): string | null {
  if (y < 2000 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const back = new Date(`${iso}T00:00:00Z`);
  return back.getUTCMonth() + 1 === m && back.getUTCDate() === d ? iso : null;
}

/**
 * Ищет в тексте письма срок ответа: «в течение 5 рабочих дней», «within 10 working days», «5 жұмыс күні ішінде»,
 * «не позднее 15.11.2026», «до 15 ноября 2026». Возвращает первое найденное; человек подтверждает или правит.
 * Дни считаются от даты письма по производственному календарю РК; срок на нерабочий день сдвигается на рабочий.
 */
export function suggestLetterTerm(text: string, letterDate: string): LetterTermSuggestion | null {
  const t = text.replace(/\s+/g, ' ');
  type Hit = { at: number; res: LetterTermSuggestion };
  const hits: Hit[] = [];

  // N дней: «в течение 5 (рабочих) дней», «в 5-дневный срок» не берём — слишком шумно
  const reDays = new RegExp(
    `(?:в течение|в\\s+срок|не\\s+позднее\\s+чем\\s+через|within|not later than)?\\s*${NUM}\\s*(?:\\(\\s*[а-яёa-z\\d]+\\s*\\)\\s*)?([а-яёәіңғүұқөһa-z.\\-]{0,14}\\s+)?(${DAYS})`,
    'gi',
  );
  for (const m of t.matchAll(reDays)) {
    const n = num(m[1] ?? '');
    if (!n || n > 120) continue;
    // Только явная оговорка срока ответа: «в течение / within / не позднее чем через» перед числом или
    // «ішінде / кешіктірмей» после него. «Срок работ 30 дней», «поставка через 5 дней» подсказкой не становятся
    const after = t.slice((m.index ?? 0) + m[0].length, (m.index ?? 0) + m[0].length + 20);
    const prefixed = /^(в течение|в\s+срок|не\s+позднее\s+чем\s+через|within|not later than)/i.test(
      m[0].trim(),
    );
    if (!prefixed && !/^\s*(ішінде|кешіктірмей)/i.test(after)) continue;
    const working = WORKING.test(m[2] ?? '');
    const date = working ? addKzWorkDays(letterDate, n) : nextWorkDay(addDays(letterDate, n));
    hits.push({
      at: m.index ?? 0,
      res: {
        date,
        kind: working ? 'working_days' : 'calendar_days',
        days: n,
        quote: m[0].trim(),
      },
    });
  }

  const yearOf = Number(letterDate.slice(0, 4));
  // Дата числами: «до 15.11.2026», «by 15/11/2026»
  const reNumDate =
    /(?:до|не позднее|не позже|by|before|no later than|дейін)\s*(\d{1,2})[./-](\d{1,2})[./-](\d{4})/gi;
  for (const m of t.matchAll(reNumDate)) {
    const iso = validDate(Number(m[3]), Number(m[2]), Number(m[1]));
    if (iso && iso >= letterDate)
      hits.push({ at: m.index ?? 0, res: { date: iso, kind: 'date', days: null, quote: m[0].trim() } });
  }
  // Дата словами: «до 15 ноября 2026», «by 15 November 2026»
  const reWordDate =
    /(?:до|не позднее|не позже|by|before|no later than|дейін)\s*(\d{1,2})\s+([а-яёәіңғүұқөһa-z]+)(?:\s+(\d{4}))?/gi;
  for (const m of t.matchAll(reWordDate)) {
    const mon = MONTHS[(m[2] ?? '').toLowerCase()];
    if (!mon) continue;
    const iso = validDate(Number(m[3] ?? yearOf), mon, Number(m[1]));
    if (iso && iso >= letterDate)
      hits.push({ at: m.index ?? 0, res: { date: iso, kind: 'date', days: null, quote: m[0].trim() } });
  }

  hits.sort((a, b) => a.at - b.at);
  return hits[0]?.res ?? null;
}
